import type * as Immutable from 'immutable';
import { createMigration } from './utils';

/**
 * Renames 'readingDirection' to 'textDirection' in text asset styles.
 */
function renameReadingDirectionInStyle(
    map: Immutable.Map<string, unknown>,
    stylePath: Array<string | number>,
) {
    const style = map.getIn(stylePath) as Immutable.Map<string, unknown> | undefined;
    if (!style) {
        return;
    }

    const readingDirection = style.get('readingDirection');
    if (readingDirection !== undefined) {
        map.setIn([...stylePath, 'textDirection'], readingDirection);
        map.deleteIn([...stylePath, 'readingDirection']);
    }
}

const TEXT_ASSET_CLASS_TYPES = ['CanvasTextAsset', 'CanvasCaptionAsset', 'CanvasFancyCaptionAsset'];

function migrateAsset(map: Immutable.Map<string, unknown>, assetPath: Array<string | number>) {
    const asset = map.getIn(assetPath) as Immutable.Map<string, unknown> | undefined;
    if (!asset) {
        return;
    }

    const classType = asset.get('classType') as string;
    if (!TEXT_ASSET_CLASS_TYPES.includes(classType)) {
        return;
    }

    renameReadingDirectionInStyle(map, [...assetPath, 'normalStyle']);
    renameReadingDirectionInStyle(map, [...assetPath, 'highlightStyle']);
    renameReadingDirectionInStyle(map, [...assetPath, 'spokenWordStyle']);
}

function migrateChildren(
    map: Immutable.Map<string, unknown>,
    childrenPath: Array<string | number>,
) {
    const children = map.getIn(childrenPath) as Immutable.List<unknown> | undefined;
    if (!children) {
        return;
    }

    children.forEach((_child: unknown, index: number) => {
        migrateAsset(map, [...childrenPath, index]);
    });
}

export const migrationTo9_234 = createMigration({
    migrateVideo(video) {
        return video.withMutations((map) => {
            map.get('sceneGroups').forEach((_sceneGroup: unknown, i: number) => {
                map.setIn(
                    ['sceneGroups', i],
                    this.migrateSceneGroup(map.getIn(['sceneGroups', i])),
                );
            });
            map.get('slides').forEach((_scene: unknown, i: number) => {
                map.setIn(['slides', i], this.migrateScene(map.getIn(['slides', i])));
            });
        });
    },
    migrateSceneGroup(sceneGroup) {
        return sceneGroup.withMutations((map) => {
            // Migrate caption asset's styles
            migrateAsset(map, ['captions', 'asset']);
        });
    },
    migrateScene(scene) {
        return scene.withMutations((map) => {
            // Migrate children in scene
            migrateChildren(map, ['children']);

            // Migrate children in subslides
            const subslides = map.get('subslides') as Immutable.List<unknown> | undefined;
            if (subslides) {
                subslides.forEach((_subslide: unknown, sIndex: number) => {
                    migrateChildren(map, ['subslides', sIndex, 'children']);
                });
            }
        });
    },
});
